import { useEffect, useRef } from 'react';

import { notify } from '@/lib/notify.js';

import { useInvalidateConnections } from './useConnections.js';

/** The Quality connect dialog keeps accepted credential writes attached to their owner. */
export function useCredentialAction(
  explainUnavailableActions: boolean,
  owner: string,
  onBusyChange?: (busy: boolean) => void,
) {
  const invalidate = useInvalidateConnections();
  const active = useRef<{ owner: string } | null>(null);
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  const mounted = useRef(true);
  const busyChanged = useRef(onBusyChange);
  busyChanged.current = onBusyChange;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      busyChanged.current?.(false);
    };
  }, []);
  const blocked = (): boolean => {
    if (!active.current) return false;
    if (explainUnavailableActions) {
      notify.warning(
        'A connection request is already running. Wait for it to finish before trying again.',
      );
    }
    return true;
  };
  const begin = () => {
    if (blocked()) return null;
    const ticket = { owner };
    active.current = ticket;
    busyChanged.current?.(true);
    return ticket;
  };
  const isCurrent = (ticket: { owner: string }) =>
    mounted.current &&
    currentOwner.current === ticket.owner &&
    active.current === ticket;
  const complete = (ticket: { owner: string }, onSaved: () => void): void => {
    void invalidate();
    if (isCurrent(ticket)) onSaved();
  };
  const end = (ticket: { owner: string }) => {
    if (active.current !== ticket) return;
    active.current = null;
    if (mounted.current) busyChanged.current?.(false);
  };
  const cancel = (onCancel: () => void) => {
    if (active.current && explainUnavailableActions) {
      notify.warning(
        'The connection request has already been sent and cannot be canceled. Wait for it to finish, then close this dialog.',
      );
      return;
    }
    onCancel();
  };
  return { blocked, begin, isCurrent, complete, end, cancel };
}
